// Core packages
import { CollectionViewer } from '@angular/cdk/collections';
import { DataSource } from '@angular/cdk/table';

// Third party packages
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

// Custom packages
import { InformativeNoteCompany, InformativeNoteService } from 'src/app/modules/companies/informative-note.service';
import { HelperService } from 'src/app/shared/services/helper.service';

/**
 * "Nota informativa" companies list data source
 *
 * @see https://blog.angular-university.io/angular-material-data-table/
 */
export class InformativeNoteCompaniesDataSource implements DataSource<InformativeNoteCompany> {
  private itemsSubject$ = new BehaviorSubject<InformativeNoteCompany[]>([]);
  private totalCountSubject$ = new BehaviorSubject<number>(0);
  private loadingSubject$ = new BehaviorSubject<boolean>(true);
  // Only the latest request may update the table (older, slower responses are dropped)
  private currentRequest: Subscription | null = null;

  public loading$ = this.loadingSubject$.asObservable();
  public totalCount$ = this.totalCountSubject$.asObservable();

  /**
   * Class constructor
   */
  constructor(
    private informativeNoteService: InformativeNoteService,
    private helperService: HelperService
  ) { }

  /**
   * Connect data table to itemsSubject$
   *
   * @since 3.0.0
   */
  connect(collectionViewer: CollectionViewer): Observable<InformativeNoteCompany[]> {
    return this.itemsSubject$.asObservable();
  }

  /**
   * Disconnect data table from subjects
   *
   * @since 3.0.0
   */
  disconnect(collectionViewer: CollectionViewer): void {
    if (this.currentRequest) {
      this.currentRequest.unsubscribe();
      this.currentRequest = null;
    }
    this.itemsSubject$.complete();
    this.totalCountSubject$.complete();
    this.loadingSubject$.complete();
  }

  /**
   * Load items from back-end
   *
   * @since 3.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filters (only "general" is supported by the back-end)
   */
  loadItems(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: { [key: string]: string }): void {
    if (this.currentRequest) {
      this.currentRequest.unsubscribe();
    }
    this.loadingSubject$.next(true);
    this.currentRequest = this.informativeNoteService.getList(start, limit, sort, sortVersus, search)
      .pipe(
        catchError(err => { // If errors, show them and return an empty list
          this.helperService.handleError(err);
          return of({ status: true, message: '', data: { items: [], totalCount: 0 } });
        }),
        finalize(() => this.loadingSubject$.next(false))
      )
      .subscribe(result => {
        const items = result && result.data && Array.isArray(result.data.items) ? result.data.items : [];
        const totalCount = result && result.data && typeof result.data.totalCount === 'number' ? result.data.totalCount : 0;
        this.itemsSubject$.next(items);
        this.totalCountSubject$.next(totalCount);
      });
  }
}
